import PropTypes from 'prop-types';
import React from 'react';
import { Steps, Popover, Button } from 'antd';
import { css } from 'emotion';
import $ from 'jquery';
import Flexbox from 'flexbox-react';
import { colorLightGrey, colorTextGrey, popoverErrorStyle, colorGreen, colorLabelBlue } from 'styles/base';

const navigation = css`
    flex-shrink:0;
    h4 {
        color: white;
        font-weight: 700 !important;
        font-size: 15px !important;
        flex: 0 0 auto;
        margin: 0 50px 0 0;
    }
    
    .ant-steps-item-title {
        white-space: nowrap;
    }
    
    .ant-steps-item-wait, .ant-steps-item-process, .ant-steps-item-finish, .ant-steps-item-error {
        > .ant-steps-item-content > .ant-steps-item-title {
            color: black;
        }
    }
    
    .ant-steps-item-finish.active, .ant-steps-item-error.active {
        > .ant-steps-item-content > .ant-steps-item-title {
            color: white;
        }
    }
    
    .ant-steps-item-finish.disabled, .ant-steps-item-error.disabled {
        > .ant-steps-item-content > .ant-steps-item-title {
             color: ${colorTextGrey};
        }
    }
    
    .ant-steps-item-error .ant-steps-item-icon {
        background-color: rgba(180,4,4,0.6);
        border-color: rgba(180,4,4, 0.3);
    }
    .ant-steps-item-error.active .ant-steps-item-icon {
        background-color: rgba(180,4,4,1);
        border-color: rgba(180,4,4,1);
    }
    .ant-steps-item-error .ant-steps-item-icon > .ant-steps-icon {
        color: white;
    }
    .ant-steps-item-finish .ant-steps-item-icon {
        background-color: rgba(0,189,112,0.6);
        border-color: rgba(0,189,112,0.3);
    }
    .ant-steps-item-finish.active .ant-steps-item-icon {
        background-color: rgba(0,189,112,1);
        border-color: rgba(0,189,112,1);
    }
    .ant-steps-item-finish .ant-steps-item-icon > .ant-steps-icon {
        color: white;
    }
    
    .ant-steps-vertical > .ant-steps-item > .ant-steps-item-tail::after {
        display: none;
    }
    .ant-steps-vertical .ant-steps-item-content {
        min-height: 0;
    }
    
    .ant-steps-item {
        padding: 7px 10px;
        border-bottom: 1px solid ${colorLightGrey};
        &:hover {
            cursor: pointer;
        }
    }
    
    .ant-steps-item.active {
        background-color: ${colorGreen};
        color: white;
    }
    
    .ant-steps-item.disabled {
        cursor: not-allowed;
    }
    
    .step-dimension-label {
        font-weight:bold;
        color:${colorLabelBlue};
    }
    
    .ant-steps-item.active .step-dimension-label {
        color: #fff;
    }
   
`;

const header = css`
    height: 50px;
    border-bottom: 1px solid ${colorLightGrey};
`;

const footerStyles = css`
    flex-shrink: 0;
    flex: initial;
    width: 100%;
    bottom: 60px;
    background-color: white;
`;

const buttonBar = css`
    padding: 10px;
    display: flex;
    flex: 1;
    flex-direction: row;
    justify-content: flex-end;
    button {
        margin: 0 2px;
    }
`;

const Step = Steps.Step;

/**
 * Page-based wizard component
 */
export default class Wizard extends React.Component {
    static propTypes = {
        title: PropTypes.string,       // the title of the wizard
        onOk: PropTypes.func,
        onCancel: PropTypes.func
    };

    static defaultProps = {
        title: 'Wizard'
    };

    constructor(props) {
        super(props);

        this.state = {
            currentPageIndex: 0,      // the index of the page currently being shown
            currentSubpageIndex: 0
        };

        this.subPages = {};
        this.nextTooltip = ''; // if you can't navigate, show the reason why to the user
    }

    /**
     * ATTENTION!!!  This is super weird, but because the wizard pages are reassiging the beforeNavigate wizard method
     * in their constructors:
     *       i.e., props.wizardInstance.beforeNavigate = this.beforeNavigate;
     *
     *       //TODO: we removed beforeNavigate is this still necessary?
     *
     * This is messing up the binding to 'this' in the wizard!  So when we call super.componentDidMount on
     * the NewProcessWizard, the 'super' is redirecting to the InputsPage, so the this method is never being called.
     *
     * So as a temporary fix, we are changing these lifecycle methods to arrow functions to resolve the 'this' problem.
     * However, this is a red flag for problems in the future, so we need to refactor how the beforeNavigate method is
     * set to prevent problems from creeping up.
     *
     * @param nextProps
     */
    componentDidMount = () => {
        this.addStepNavigation();
    };

    componentDidUpdate = () => {
        this.addStepNavigation();
    };

    /**
     * Get the props that this wizard should pass to its children
     * Children should override to pass their specific properties
     * @returns {object}
     */
    getChildProps() {
        const pageAttributes = this.getPages()[this.state.currentPageIndex];
        const subPages = this.getSubPages(this.getPages()[this.state.currentPageIndex].id);
        const subPageName = subPages[this.state.currentSubpageIndex] ? subPages[this.state.currentSubpageIndex] : '';
        return {
            pageAttributes,
            subPageName
        };
    }

    /**
     * Children should override if they want to show progress steps at the bottom of the wizard.  Each page can
     * be mapped to the associated step.
     * @returns {Array[string]}
     */
    getSteps() {
        return [];
    }

    /**
     * Children should override to provide the list of pages available to the wizard.  This lets them
     * dynamically change which pages are available.
     * @returns {Array[Object]}
     */
    getPages() {
        return [];
    }

    getCurrentPage() {
        return this.getPages()[this.state.currentPageIndex];
    }

    /**
     * @param pageId string
     * @returns {Array[string]}
     * Child class should override the list in subpages
     */

    getSubPages = (page) => {
        return [];
    };

    getPageForStep(stepName) {
        const steps = this.getSteps();
        const pages = this.getPages();
        let pageIndex = 0;

        // find the first page that has that step
        for (let i = 0; i < pages.length; i++) {
            const page = pages[i];
            if (steps[page.stepPos].key === stepName) {
                pageIndex = i;
                break;
            }
        }
        return pageIndex;
    }

    getStepErrors() {
        const stepErrors = {};
        const stepsList = this.getSteps();
        stepsList.forEach(step => {
            stepErrors[step.key] = this.hasStepError(step.key, step.subPage);
        });
        return stepErrors;
    }

    /**
     * Children can override to evaluate if the given step has an error condition that should be rendered.
     * @param stepName
     * @returns {boolean}
     */
    hasStepError(stepName, dimensionName) {
        return false;
    }

    /**
     * Children can override to dynamically determine if all data has been filled out correctly and the wizard
     * is safe to close.
     * @returns {boolean}
     */
    canFinish() {
        const stepErrors = this.getStepErrors();
        let canFinish = true;
        Object.keys(stepErrors).forEach(step => {
            if (stepErrors[step]) {
                canFinish = false;
            }
        });
        return canFinish;
    }

    canGoBack() {
        return this.state.currentPageIndex > 0;
    }

    canGoForward() {
        const pages = this.getPages();
        const index = this.state.currentPageIndex;
        let canGoForward = false;
        if (!this.isLastPage() && !this.isMissingRequiredInfo(pages[index].id)) {
            canGoForward = true;
        }

        return canGoForward;
    }

    canGoToPage(pageIndex) {
        // we only allow users to navigate to another page (via the steps)
        // if they have already been there before
        if (pageIndex === 0) {
            return true;
        } else {
            //none of the previous pages have missing required information
            const pages = this.getPages();
            let previousPageMissing = false;
            for (let i = 0; i < pageIndex; i++) {
                if (this.isMissingRequiredInfo(pages[i].id)) {
                    previousPageMissing = true;
                }
            }

            if (!previousPageMissing) {
                return true;
            }
        }

        return false;
    }

    isMissingRequiredInfo = (pageId) => {
        // override to prevent moving on to next page if required information is missing
        return false;
    };

    isLastPage = () => {
        const pages = this.getPages();
        const index = this.state.currentPageIndex;
        const subPageIndex = this.state.currentSubpageIndex;

        let isLastPage = true;
        if (index < pages.length - 1) {
            isLastPage = false;
        } else {
            const subPages = this.getSubPages(pages[index].id);
            if (subPageIndex < subPages.length - 1) {
                isLastPage = false;
            }
        }

        return isLastPage;
    };

    updateSubPages = (pageId, subPages) => {
        this.subPages[pageId] = subPages;
    };

    beforeFinish = () => {
        //modify process or anything that needs to be done before finishing and closing the modal
    };

    onOk = () => {
        this.beforeFinish();
        if (this.props.onOk) {
            this.props.onOk();
        }
    };

    onCancel = () => {
        if (this.props.onCancel) {
            this.props.onCancel();
        }
    };

    onBack = () => {
        if (this.canGoBack()) {
            let currentPageIndex = this.state.currentPageIndex;
            let currentSubpageIndex = this.state.currentSubpageIndex;

            const pages = this.getPages();
            const subPages = this.getSubPages(pages[currentPageIndex].id);
            const previousPageSubpages = this.getSubPages(pages[currentPageIndex - 1].id);
            if (subPages.length === 0 || currentSubpageIndex === 0) {
                currentPageIndex = currentPageIndex - 1;

                if (previousPageSubpages.length > 0) {
                    currentSubpageIndex = previousPageSubpages.length - 1;
                } else {
                    currentSubpageIndex = 0;
                }
            } else {
                currentSubpageIndex -= 1;
            }


            this.setState({ currentPageIndex, currentSubpageIndex });
        }
    };

    /**
     * IMPORTANT:  Arrow functions are not assigned to the super class prototype, so if you extend
     * a class, child classes will not be able to call the super class implementation of the arrow function.
     * (For example, if onNext was an arrow function, then the child class could not call super.onNext() if they
     * needed to override this method.)  So I changed this method to not use an arrow function and I had to add a this binding
     * in the render() method.
     *
     * In general arrow functions are fine for class methods, unless a child class needs to override them and call super()
     */
    onNext() {
        if (this.canGoForward()) {
            let currentPageIndex = this.state.currentPageIndex;
            let currentSubpageIndex = this.state.currentSubpageIndex;

            const pages = this.getPages();
            const subPages = this.getSubPages(pages[currentPageIndex].id);
            if (subPages.length === 0 || currentSubpageIndex === subPages.length - 1) {
                currentPageIndex = currentPageIndex + 1;
                currentSubpageIndex = 0; //always reset on moving to next page
            } else {
                currentSubpageIndex += 1;
            }

            this.setState({ currentPageIndex, currentSubpageIndex });
        }
    }

    goToStep(stepName) {
        const pageIndex = this.getPageForStep(stepName);
        if (this.canGoToPage(pageIndex)) {
            this.setState({ currentPageIndex: pageIndex, currentSubpageIndex: 0 });
        }
    }

    /**
     * We have to hook navigation into the Step components after they are rendered,
     * because navigation is not natively supported by these components.
     */
    addStepNavigation() {
        $('.ant-steps-item').each((index, element) => {
            const stepName = $(element).find('.ant-steps-item-title > span').attr('data-step-key');
            $(element).off('click.pcm'); // wipe any previous listeners
            //$(element).css('cursor', 'not-allowed');
            $(element).addClass('disabled');
            const pageIndex = this.getPageForStep(stepName);
            if (this.canGoToPage(pageIndex)) {
                $(element).removeClass('disabled');
                //$(element).css('cursor', 'pointer');
                $(element).on('click.pcm', () => {
                    this.goToStep(stepName);
                });
            }
        });
    }

    renderNavigationHeader() {
        //override for heading section above the side navigation
        return <div/>;
    }

    renderSteps() {
        const pages = this.getPages();
        const stepErrors = this.getStepErrors();
        const stepsList = this.getSteps();

        if (stepsList.length > 0) {
            const currentStep = pages[this.state.currentPageIndex].stepPos;
            const steps = stepsList.map((step, index) => {
                let stepStatus = '';
                if (stepErrors[step.key]) {
                    stepStatus = 'error';
                } else {
                    stepStatus = 'finish';
                }
                let className = '';
                if (index === currentStep) {
                    className = 'active';
                }
                return (
                    <Step key={step.key} title={step.title} status={stepStatus} className={className}/>
                );
            });
            return (
                <Steps direction="vertical" size="small" current={currentStep}>
                    {steps}
                </Steps>
            );

        }
        return <div/>;
    }

    renderCurrentPage() {
        const pages = this.getPages();

        // Create the current page component and pass it the current data and change handlers
        return (
            <Flexbox className={css`width:100%;`}>
                { React.createElement(pages[this.state.currentPageIndex].component, this.getChildProps()) }
            </Flexbox>
        );
    }

    renderFooterMessage() {
        // override to include content in footer of modal
        return <div/>;
    }

    renderBeforeNavButtons() {
        //override to include button in the left of footer
        return <div/>;
    }

    renderAfterNavButtons() {
        //override to include buttons in the right of footer
        return <div/>;
    }

    canOpenWizard() {
        //override if there are conditions to open in wizard
        return true;
    }

    render() {
        if (this.canOpenWizard()) {
            const canGoForward = this.canGoForward();

            //hide button on last/first page
            const displayHidden = css`visibility:hidden;`;
            const backStyles = this.state.currentPageIndex === 0 ? displayHidden : '';
            const nextStyles = this.isLastPage() ? displayHidden : '';
            const onNext = this.onNext.bind(this);

            let nextButton = (
                <Button onClick={onNext} disabled={!canGoForward} className={nextStyles}>
                    Next &gt;
                </Button>

            );
            if (!canGoForward) {
                const tooltip = this.nextTooltip ? this.nextTooltip : 'Missing required information';
                nextButton = (
                    <Popover content={tooltip} placement="top" overlayClassName={popoverErrorStyle}>
                        {nextButton}
                    </Popover>
                );
            }

            return (
                <Flexbox flexDirection={'row'} className={css`width:100%;`}>
                    <Flexbox className={`wizard-navigation ${navigation}`} flexDirection={'column'}>
                        <Flexbox className={header}>
                            {this.renderNavigationHeader()}
                        </Flexbox>
                        {this.renderSteps()}
                    </Flexbox>
                    {/* For some reason, I have to set the width of the wizard content div to a small number,
                    or else the wizard page isn't scrolling in y direction. */}
                    <Flexbox className="wizard-content" flexDirection={'column'} flexGrow={1} width={'200px'}>
                        <Flexbox className="wizard-page" flexGrow={1}>
                            {this.renderCurrentPage()}
                        </Flexbox>
                        <Flexbox className={`wizard-footer ${footerStyles}`}>
                            <Flexbox className={buttonBar}>
                                <div className={css`flex-grow:1;text-align:left;`}>
                                    {this.renderBeforeNavButtons()}
                                </div>
                                <div className={css`margin-right: 15px; margin-left:15px; flex-shrink:0;`}>
                                    <div>
                                        <Button
                                            onClick={this.onBack} disabled={!this.canGoBack()}
                                            className={backStyles}
                                        >
                                            &lt; Back
                                        </Button>
                                        {nextButton}
                                    </div>
                                </div>
                                <div className={css`flex-shrink:0; display: flex`}>
                                    {this.renderAfterNavButtons()}
                                </div>
                            </Flexbox>
                        </Flexbox>
                    </Flexbox>
                </Flexbox>
            );
        } else {
            return <div/>;
        }
    }

}




// WEBPACK FOOTER //
// ./src/components/core/wizard/Wizard.js